import React, { useEffect, useState } from 'react';
import { Icon } from './icons.jsx';
// Drawer de criação/edição de plano
const PlanEditorDrawer = ({ open, plan, onClose, onSave }) => {
  const isNew = !plan;
  const [form, setForm] = useState({
    name: '', price: '', pageviews: '', users: '',
    trialDays: '14', yearlyDiscount: '20', active: true,
    description: '', highlight: false,
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      if (plan) {
        setForm({
          name: plan.name,
          price: String(plan.price),
          pageviews: String(plan.pageviews),
          users: String(plan.users),
          trialDays: String(plan.trialDays ?? 14),
          yearlyDiscount: String(plan.yearlyDiscount ?? 20),
          active: plan.active ?? true,
          description: plan.description ?? '',
          highlight: plan.highlight ?? false,
        });
      } else {
        setForm({ name: '', price: '', pageviews: '', users: '', trialDays: '14', yearlyDiscount: '20', active: true, description: '', highlight: false });
      }
      setErrors({});
    }
  }, [open, plan]);

  if (!open) return null;

  const set = (k) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm(f => ({ ...f, [k]: v }));
    if (errors[k]) setErrors(e => ({ ...e, [k]: null }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Nome é obrigatório';
    if (!form.price || isNaN(Number(form.price)) || Number(form.price) <= 0) e.price = 'Preço inválido';
    if (!form.pageviews || isNaN(Number(form.pageviews)) || Number(form.pageviews) <= 0) e.pageviews = 'Obrigatório';
    if (!form.users || isNaN(Number(form.users)) || Number(form.users) <= 0) e.users = 'Obrigatório';
    return e;
  };

  const handleSave = () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    onSave({
      ...plan,
      id: plan?.id ?? form.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''),
      name: form.name.trim(),
      price: Number(form.price),
      pageviews: Number(form.pageviews),
      users: Number(form.users),
      trialDays: Number(form.trialDays),
      yearlyDiscount: Number(form.yearlyDiscount),
      active: form.active,
      description: form.description,
      highlight: form.highlight,
    });
  };

  const yearlyPrice = form.price && !isNaN(Number(form.price))
    ? Number(form.price) * 12 * (1 - (Number(form.yearlyDiscount) || 0) / 100)
    : null;

  const inputStyle = {
    width: '100%', height: 36, padding: '0 12px',
    borderRadius: 'var(--radius)', border: '1px solid var(--border-strong)',
    background: 'var(--bg-elevated)', color: 'var(--text)',
    fontSize: 13, fontFamily: 'inherit',
  };
  const inputErrStyle = { ...inputStyle, borderColor: 'var(--brand-red)' };
  const label = (txt, required) => (
    <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 6 }}>
      {txt}{required && <span style={{ color: 'var(--brand-red)', marginLeft: 2 }}>*</span>}
    </label>
  );
  const errMsg = (k) => errors[k] && <div style={{ fontSize: 11, color: 'var(--brand-red)', marginTop: 4 }}>{errors[k]}</div>;

  return (
    <>
      <div className="drawer-overlay" onClick={onClose} />
      <div className="drawer" style={{ width: 560 }}>
        <div className="drawer-head">
          <div style={{ width: 36, height: 36, borderRadius: 8, background: isNew ? 'var(--info-bg)' : 'var(--bg-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: isNew ? 'var(--info)' : 'var(--text-muted)' }}>
            <Icon name={isNew ? 'plus' : 'edit'} size={18} />
          </div>
          <div style={{ flex: 1 }}>
            <h3>{isNew ? 'Criar novo plano' : `Editar plano ${plan.name}`}</h3>
            <div className="sub">{isNew ? 'Configure os limites e o preço do novo plano.' : 'Alterações afetam novas assinaturas imediatamente.'}</div>
          </div>
          <button className="icon-action" onClick={onClose} style={{ width: 32, height: 32 }}><Icon name="x" size={16} /></button>
        </div>

        <div className="drawer-body">
          {/* Identificação */}
          <div className="drawer-section">
            <h4>Identificação</h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div style={{ gridColumn: '1 / -1' }}>
                {label('Nome do plano', true)}
                <input style={errors.name ? inputErrStyle : inputStyle} value={form.name} onChange={set('name')} placeholder="Ex: Business" />
                {errMsg('name')}
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                {label('Descrição curta')}
                <input style={inputStyle} value={form.description} onChange={set('description')} placeholder="Ex: Para lojas em crescimento acelerado" />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 16, marginTop: 14 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
                <input type="checkbox" checked={form.active} onChange={set('active')} style={{ width: 15, height: 15, accentColor: 'var(--brand-navy)' }} />
                <span>Plano ativo (disponível para novas assinaturas)</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
                <input type="checkbox" checked={form.highlight} onChange={set('highlight')} style={{ width: 15, height: 15, accentColor: 'var(--brand-navy)' }} />
                <span>Destacar como recomendado</span>
              </label>
            </div>
          </div>

          {/* Preço */}
          <div className="drawer-section">
            <h4>Preços</h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div>
                {label('Preço mensal (R$)', true)}
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', fontSize: 13 }}>R$</span>
                  <input style={{ ...(errors.price ? inputErrStyle : inputStyle), paddingLeft: 32 }} type="number" min="0" step="0.01" value={form.price} onChange={set('price')} placeholder="0,00" />
                </div>
                {errMsg('price')}
              </div>
              <div>
                {label('Desconto anual (%)')}
                <div style={{ position: 'relative' }}>
                  <input style={{ ...inputStyle, paddingRight: 28 }} type="number" min="0" max="100" value={form.yearlyDiscount} onChange={set('yearlyDiscount')} />
                  <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', fontSize: 13 }}>%</span>
                </div>
              </div>
            </div>

            {yearlyPrice !== null && Number(form.price) > 0 && (
              <div className="summary-box" style={{ marginTop: 12 }}>
                <div className="row">
                  <span style={{ color: 'var(--text-muted)' }}>Mensal</span>
                  <span style={{ fontWeight: 500 }}>{fmtBRL(Number(form.price))}/mês</span>
                </div>
                <div className="row">
                  <span style={{ color: 'var(--text-muted)' }}>Anual ({form.yearlyDiscount}% off)</span>
                  <span style={{ fontWeight: 500 }}>{fmtBRL(yearlyPrice)}/ano <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>≈ {fmtBRL(yearlyPrice / 12)}/mês</span></span>
                </div>
              </div>
            )}

            <div style={{ marginTop: 14 }}>
              {label('Trial gratuito (dias)')}
              <div className="radio-card-row">
                {[0, 7, 14, 30].map(d => (
                  <div key={d} className={'radio-card ' + (String(form.trialDays) === String(d) ? 'selected' : '')} style={{ textAlign: 'center', padding: '8px 0' }} onClick={() => setForm(f => ({ ...f, trialDays: String(d) }))}>
                    <div className="title">{d === 0 ? 'Sem trial' : `${d} dias`}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Limites */}
          <div className="drawer-section">
            <h4>Limites de uso</h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div>
                {label('Pageviews/mês', true)}
                <input style={errors.pageviews ? inputErrStyle : inputStyle} type="number" min="0" value={form.pageviews} onChange={set('pageviews')} placeholder="10000" />
                {errMsg('pageviews')}
              </div>
              <div>
                {label('Usuários internos', true)}
                <input style={errors.users ? inputErrStyle : inputStyle} type="number" min="1" value={form.users} onChange={set('users')} placeholder="2" />
                {errMsg('users')}
              </div>
            </div>

            {/* Preview dos limites */}
            {form.pageviews && form.users && (
              <div style={{ marginTop: 14, padding: '12px 14px', borderRadius: 'var(--radius)', border: '1px dashed var(--border-strong)', background: 'var(--bg-subtle)' }}>
                <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted)', marginBottom: 8 }}>Preview do plano</div>
                <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', fontSize: 13 }}>
                  <div><span style={{ color: 'var(--text-muted)' }}>Pageviews </span><strong>{Number(form.pageviews).toLocaleString('pt-BR')}</strong></div>
                  <div><span style={{ color: 'var(--text-muted)' }}>Usuários </span><strong>{form.users}</strong></div>
                  {form.trialDays > 0 && <div><span style={{ color: 'var(--text-muted)' }}>Trial </span><strong>{form.trialDays} dias</strong></div>}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="drawer-foot">
          <span className="note">
            {isNew ? 'O plano ficará disponível para novas assinaturas imediatamente.' : 'Assinaturas existentes não são afetadas automaticamente.'}
          </span>
          <button className="btn ghost" onClick={onClose}>Cancelar</button>
          <button className="btn primary" onClick={handleSave}>
            {isNew ? <><Icon name="plus" size={14} />Criar plano</> : <><Icon name="check" size={14} />Salvar alterações</>}
          </button>
        </div>
      </div>
    </>
  );
};

export {
  PlanEditorDrawer,
};
